import { Table, TableWrapper, Tbody, Td, Th, Thead, Tr } from '@components/core-ui/Table';
import { Header } from '@components/Header';
import Add from '@components/icons/Add.svg';
import AddBlue from '@components/icons/AddBlue.svg';
import PencilIcon from '@components/icons/Pencil.svg';
import { PageErrorState } from '@components/core-ui/PageErrorState';
import { LinearProgressLoader } from '@components/core-ui/spinners/LinearProgressLoader';
import { TableStyledLink } from '@components/core-ui/Table/StyledLink';
import { Tag } from '@components/core-ui/Tag';
import { useAuthContext } from '@providers/AuthProvider';
import { useModalContext } from '@providers/ModalProvider';
import { useAPI } from '@providers/useAPI';
import { routes } from '@routes';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { OrganizationTeamResponseDto, PrismaWorkspaceDto, WorkspacesService } from 'app/API';
import { cancelAndInvalidateQueries } from 'app/queries/cancelAndInvalidateQueries';
import { TTeamMembersChange } from '@modals/Team/EditTeamMembersModal/types/TTeamMembersChange';
import { commitTeamMembers } from 'app/queries/teams/commitTeamMembers';
import { createFetchOrganizationTeamsQueryFn } from 'app/queries/teams/createFetchOrganizationTeamsQueryFn';
import { teamsKeys } from 'app/queries/teams/teamsKeys';
import { ModalNamesEnum } from 'app/types/enums/ModalNamesEnum';
import { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';

import { filterTeamsByName } from './helpers/filterTeamsByName';
import { findTeamWorkspace } from './helpers/findTeamWorkspace';
import { updateTeamMemberCount } from './helpers/updateTeamMemberCount';
import { sortWorkspacesByName } from './helpers/sortWorkspacesByName';
import { updateTeamWorkspaces } from './helpers/updateTeamWorkspaces';
import { FlexCellContent } from './styles/FlexCellContent';
import { MainContent } from './styles/MainContent';
import { TDetachedWorkspaceContext } from './types/TDetachedWorkspaceContext';
import { TOptimisticWorkspaceContext } from './types/TOptimisticWorkspaceContext';
import { TTeamMembersUpdate } from './types/TTeamMembersUpdate';
import { TWorkspaceTeamLink } from './types/TWorkspaceTeamLink';

const NO_WORKSPACES: PrismaWorkspaceDto[] = [];

export const TeamsPage = () => {
  const { call } = useAPI();
  const { organization } = useAuthContext();
  const { openModal, closeModal } = useModalContext();
  const queryClient = useQueryClient();

  const [search, setSearch] = useState('');

  const organizationId = organization?.id ?? '';
  const orgSlug = organization?.slug ?? '';

  const teamsQuery = useQuery({
    queryKey: teamsKeys.listsByOrganization(organizationId),
    queryFn: createFetchOrganizationTeamsQueryFn(call),
    enabled: organizationId !== '',
  });

  const teamsListKey = teamsKeys.listsByOrganization(organizationId);

  const refreshTeams = () => cancelAndInvalidateQueries(queryClient, teamsListKey);

  const cancelTeamsQuery = () => queryClient.cancelQueries({ queryKey: teamsListKey });

  const writeTeamWorkspaces = (
    teamId: string,
    updateWorkspaces: (workspaces: PrismaWorkspaceDto[]) => PrismaWorkspaceDto[],
  ) =>
    queryClient.setQueryData<OrganizationTeamResponseDto[]>(teamsListKey, (teams) =>
      updateTeamWorkspaces(teams, teamId, updateWorkspaces),
    );

  const writeTeamMemberCount = (teamId: string, delta: number) =>
    queryClient.setQueryData<OrganizationTeamResponseDto[]>(teamsListKey, (teams) =>
      updateTeamMemberCount(teams, teamId, delta),
    );

  const withoutWorkspace = (workspaceSlug: string) => (workspaces: PrismaWorkspaceDto[]) =>
    workspaces.filter((workspace) => workspace.slug !== workspaceSlug);

  const attachWorkspaceMutation = useMutation({
    mutationFn: ({ teamId, workspaceSlug }: TWorkspaceTeamLink) =>
      call(
        () =>
          WorkspacesService.updateWorkspaceByAddingTeam({
            orgSlug,
            wsSlug: workspaceSlug,
            requestBody: { teamIds: [teamId] },
          }),
        { onError: (message) => toast.error(message || 'Unable to attach the workspace.') },
      ),
    onMutate: async ({ teamId, workspaceSlug }: TWorkspaceTeamLink): Promise<TOptimisticWorkspaceContext> => {
      await cancelTeamsQuery();

      const attached = organization?.workspaces?.find((workspace) => workspace.slug === workspaceSlug);

      if (!attached) {
        return { applied: false };
      }

      writeTeamWorkspaces(teamId, (workspaces) => [...workspaces, attached]);

      return { applied: true };
    },
    onError: (_error, { teamId, workspaceSlug }, context) => {
      if (context?.applied) {
        writeTeamWorkspaces(teamId, withoutWorkspace(workspaceSlug));
      }
    },
    onSettled: refreshTeams,
  });

  const disconnectWorkspaceMutation = useMutation({
    mutationFn: ({ teamId, workspaceSlug }: TWorkspaceTeamLink) =>
      call(() => WorkspacesService.updateWorkspaceByRemovingTeam({ orgSlug, wsSlug: workspaceSlug, teamId }), {
        onError: (message) => toast.error(message || 'Unable to disconnect the workspace.'),
      }),
    onMutate: async ({ teamId, workspaceSlug }: TWorkspaceTeamLink): Promise<TDetachedWorkspaceContext> => {
      await cancelTeamsQuery();

      const detached = findTeamWorkspace(
        queryClient.getQueryData<OrganizationTeamResponseDto[]>(teamsListKey),
        teamId,
        workspaceSlug,
      );

      writeTeamWorkspaces(teamId, withoutWorkspace(workspaceSlug));

      return { detached };
    },
    onError: (_error, { teamId }, context) => {
      const { detached } = context ?? {};

      if (detached) {
        writeTeamWorkspaces(teamId, (workspaces) => [...workspaces, detached]);
      }
    },
    onSettled: refreshTeams,
  });

  const updateMembersMutation = useMutation({
    mutationFn: ({ teamId, added, removed }: TTeamMembersUpdate) =>
      commitTeamMembers(
        {
          teamId,
          added,
          removed,
          onError: (message) => toast.error(message || 'Unable to update the team members.'),
        },
        call,
      ),
    onMutate: async ({ teamId, added, removed }: TTeamMembersUpdate) => {
      await cancelTeamsQuery();

      writeTeamMemberCount(teamId, added.length - removed.length);
    },
    onError: (_error, { teamId, added, removed }) => writeTeamMemberCount(teamId, removed.length - added.length),
    onSettled: refreshTeams,
  });

  const teams = filterTeamsByName(teamsQuery.data ?? [], search);
  const isWorkspaceBusy = attachWorkspaceMutation.isPending || disconnectWorkspaceMutation.isPending;
  const isBusy = teamsQuery.isFetching || isWorkspaceBusy || updateMembersMutation.isPending;
  const hasInitialError = teamsQuery.isError && teamsQuery.data === undefined;

  const editMembers = (teamId: string) => {
    openModal(ModalNamesEnum.EditTeamMembersModal, {
      teamId,
      onSuccess: ({ added, removed }: TTeamMembersChange) => {
        closeModal();

        if (added.length === 0 && removed.length === 0) {
          return;
        }

        updateMembersMutation.mutate({ teamId, added: added.map((member) => member.id), removed });
      },
    });
  };

  const addWorkspace = (teamId: string, workspaceIds: string[]) => {
    openModal(ModalNamesEnum.AddWSToTeamModal, {
      workspaceIds,
      onSuccess: (workspaceSlug: string) => {
        closeModal();
        attachWorkspaceMutation.mutate({ teamId, workspaceSlug });
      },
    });
  };

  const disconnectWorkspace = (teamId: string, workspaceSlug: string) => {
    openModal(ModalNamesEnum.CustomConfirmationModal, {
      description: 'Are you sure you want to disconnect this workspace?',
      buttonText: 'Disconnect',
      onSuccess: () => {
        closeModal();
        disconnectWorkspaceMutation.mutate({ teamId, workspaceSlug });
      },
    });
  };

  const createNewTeam = () => {
    openModal(ModalNamesEnum.NewTeamModal, {
      onSuccess: () => {
        closeModal();
        void refreshTeams();
      },
    });
  };

  return (
    <>
      <Helmet>
        <title>Teams - AutoScript</title>
      </Helmet>
      <Header
        title="Teams"
        setSearch={setSearch}
        search={search}
        buttonLabel={'Create New Team'}
        buttonIcon={Add}
        buttonOnClick={() => createNewTeam()}
      />

      <LinearProgressLoader active={isBusy} />
      <MainContent>
        {hasInitialError ? (
          <PageErrorState
            message="Couldn't load teams"
            actionLabel="Retry"
            actionDisabled={isBusy}
            onAction={() => void teamsQuery.refetch()}
          />
        ) : (
          <TableWrapper>
            <Table>
              <Thead>
                <Tr>
                  <Th style={{ width: '20%' }}>Team</Th>
                  <Th style={{ width: '10%' }}>Workspaces</Th>
                  <Th style={{ width: '1%' }}>Members</Th>
                </Tr>
              </Thead>
              <Tbody>
                {teams.map((data) => (
                  <Tr key={data.id}>
                    <Td>
                      <FlexCellContent>
                        <TableStyledLink
                          to={routes.teamDetails.make(organization?.slug ?? '', data.slug)}
                          state={{ from: routes.organizationTeams.make(organization?.slug ?? '') }}
                        >
                          {data.name}
                        </TableStyledLink>
                      </FlexCellContent>
                    </Td>
                    <Td>
                      <FlexCellContent className="flex-wrap gap-2">
                        {sortWorkspacesByName(data.Workspaces ?? NO_WORKSPACES).map((workspace) => (
                          <Tag
                            key={workspace.id}
                            text={workspace.name}
                            deleteDisabled={isWorkspaceBusy}
                            onDelete={() => disconnectWorkspace(data.id, workspace.slug)}
                            link={routes.workspaceDetails.make(orgSlug, workspace.slug)}
                          />
                        ))}
                        <button
                          type="button"
                          aria-label="Attach workspace"
                          className="shrink-0 text-foreground-muted enabled:cursor-pointer enabled:hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={isWorkspaceBusy}
                          onClick={() =>
                            addWorkspace(
                              data.id,
                              (data.Workspaces ?? NO_WORKSPACES).map((workspace) => workspace.id),
                            )
                          }
                        >
                          <AddBlue className="size-4" />
                        </button>
                      </FlexCellContent>
                    </Td>
                    <Td>
                      <FlexCellContent>
                        {data.members || 0}
                        <button
                          type="button"
                          aria-label="Edit members"
                          className="ml-1.5 text-foreground-muted enabled:cursor-pointer enabled:hover:text-accent disabled:cursor-not-allowed disabled:opacity-60"
                          disabled={updateMembersMutation.isPending}
                          onClick={() => editMembers(data.id)}
                        >
                          <PencilIcon />
                        </button>
                      </FlexCellContent>
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableWrapper>
        )}
      </MainContent>
    </>
  );
};
